import React, { useState } from "react";
import {
  Box,
  Container,
  Text,
  TextInput,
  Button,
  Grid,
  Card,
  Group,
  Badge,
  Table,
  Checkbox,
  NumberInput,
  Alert,
  Divider,
  Paper,
  ActionIcon,
  Tooltip,
  Select,
} from "@mantine/core";
import {
  IconSearch,
  IconAlertCircle,
  IconArrowRight,
  IconUser,
  IconReceipt,
  IconTruckReturn,
  IconCircleCheck,
} from "@tabler/icons";
import { useNavigate } from "react-router";
import AppBreadcrumb from "components/AppBreadcrumb";
import { return_api } from "./return.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";

export default function SalesReturn() {
  const [invoiceNo, setInvoiceNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [returnQtys, setReturnQtys] = useState({});
  const [productSearch, setProductSearch] = useState(null);
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!invoiceNo.trim()) {
      showErrorToast({ title: "Validation Error", message: "Please enter an invoice number." });
      return;
    }
    setLoading(true);
    setInvoiceData(null);
    setSelectedItems({});
    setReturnQtys({});
    setProductSearch(null);

    try {
      const response = await return_api.lookup_invoice(invoiceNo.trim());
      if (response && response.success) {
        setInvoiceData(response.data);
        // Pre-initialize return quantities with original quantities
        const qtys = {};
        response.data.items.forEach(item => {
          qtys[item.id] = item.qty;
        });
        setReturnQtys(qtys);
        showSuccessToast({ title: "Invoice Found", message: "Invoice details loaded successfully." });
      } else {
        showErrorToast({
          title: "Not Found",
          message: response?.message || "Invoice not found or invalid.",
        });
      }
    } catch (error) {
      showErrorToast({ title: "Error", message: "Failed to search invoice." });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId, checked) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: checked,
    }));
  };

  const handleQtyChange = (itemId, val, maxVal) => {
    const cleanVal = parseFloat(val) || 0;
    if (cleanVal > maxVal) {
      setReturnQtys(prev => ({ ...prev, [itemId]: maxVal }));
    } else if (cleanVal < 0) {
      setReturnQtys(prev => ({ ...prev, [itemId]: 0 }));
    } else {
      setReturnQtys(prev => ({ ...prev, [itemId]: cleanVal }));
    }
  };

  // Calculate total return value dynamically
  const calculateTotalRefund = () => {
    if (!invoiceData) return 0;
    let total = 0;
    invoiceData.items.forEach(item => {
      if (selectedItems[item.id]) {
        const qty = returnQtys[item.id] || 0;
        total += qty * item.rate;
      }
    });
    return parseFloat(total.toFixed(2));
  };

  const handleProceedExchange = () => {
    const selectedList = invoiceData.items.filter(item => selectedItems[item.id] && (returnQtys[item.id] > 0));
    if (selectedList.length === 0) {
      showErrorToast({
        title: "No Items Selected",
        message: "Please select at least one item with a valid return quantity.",
      });
      return;
    }

    const exchangeItems = selectedList.map(item => ({
      product_id: item.product_id,
      product_title: item.product_title,
      product_code: item.product_code,
      qty: returnQtys[item.id],
      original_qty: item.qty,
      rate: item.rate,
      amount: returnQtys[item.id] * item.rate,
      counter_stock_id: item.counter_stock_id,
      counter_stock_batch_id: item.counter_stock_batch_id,
      batch_code: item.batch_code,
      units: item.units,
      mrp: item.mrp || 0,
      expiry_date: item.expiry_date || "",
      stock: item.stock || 0,
      tax: item.tax || 0,
    }));

    const totalRefundCredit = calculateTotalRefund();

    // Redirect to invoice master billing page and pass the exchange payload
    navigate("/invoices/add", {
      state: {
        isExchangeFlow: true,
        originalInvoiceId: invoiceData.invoice.id,
        originalInvoiceNo: invoiceData.invoice.invoice_id,
        exchangeItems: exchangeItems,
        totalRefundCredit: totalRefundCredit,
        customer: invoiceData.customer,
      },
    });
  };

  return (
    <Box px={32} py={24} style={{ minHeight: "90vh" }}>
      <Container fluid p={0}>
        <Box mb={24}>
        <Text size="xl" fw={700} style={{ color: "#2B2D42", letterSpacing: "0.5px" }}>
          Sales Return & Exchange
        </Text>
        <AppBreadcrumb
          crumbs={[
            { text: "Transactions" },
            { text: "Sales Return" },
          ]}
        />
      </Box>

      {/* Modern Search Card with Premium Styling */}
      <Paper
        shadow="xl"
        p={30}
        radius="lg"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Grid align="flex-end" gutter="md">
          <Grid.Col xs={12} sm={8} md={9}>
            <TextInput
              label="Enter Invoice Number"
              placeholder="e.g. INV-10024"
              value={invoiceNo}
              onChange={e => setInvoiceNo(e.target.value)}
              size="lg"
              radius="md"
              icon={<IconReceipt size={20} color="#718096" />}
              style={{ flexGrow: 1 }}
              onKeyDown={e => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col xs={12} sm={4} md={3}>
            <Button
              fullWidth
              size="lg"
              radius="md"
              onClick={handleSearch}
              loading={loading}
              leftIcon={<IconSearch size={18} />}
              style={{
                backgroundColor: "#4F46E5",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#4338CA",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Search Bill
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {invoiceData && (
        <Box mt={32}>
          
          <Grid gutter="lg">
            <Grid.Col md={6}>
              <Card
                shadow="md"
                p="xl"
                radius="lg"
                style={{
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  height: "100%",
                }}
              >
                <Group position="apart" mb="md">
                  <Text fw={700} size="lg" style={{ color: "#1A202C" }}>
                    Invoice Overview
                  </Text>
                  <Badge size="lg" variant="dot" color="indigo">
                    {invoiceData.invoice.invoice_id}
                  </Badge>
                </Group>
                <Divider mb="md" />
                <Grid gutter="sm">
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Purchase Date</Text>
                    <Text fw={600}>{new Date(invoiceData.invoice.create_at).toLocaleDateString("en-GB", {
                      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                    })}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Balance</Text>
                    <Text fw={700} color={(invoiceData.invoice.balance || 0) > 0 ? "red" : "teal"}>₹ {(invoiceData.invoice.balance || 0).toFixed(2)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Sub Total</Text>
                    <Text fw={600}>₹ {invoiceData.invoice.sub_total.toFixed(2)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Discount Total</Text>
                    <Text fw={600} color="red">₹ {invoiceData.invoice.discount_total.toFixed(2)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Tax Total</Text>
                    <Text fw={600}>₹ {invoiceData.invoice.tax_total.toFixed(2)}</Text>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text size="xs" color="dimmed">Grand Total</Text>
                    <Text fw={700} size="md" color="teal">₹ {invoiceData.invoice.total.toFixed(2)}</Text>
                  </Grid.Col>
                </Grid>
              </Card>
            </Grid.Col>

            <Grid.Col md={6}>
              <Card
                shadow="md"
                p="xl"
                radius="lg"
                style={{
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Group position="apart" mb="md">
                    <Text fw={700} size="lg" style={{ color: "#1A202C" }}>
                      Customer Info & Validation
                    </Text>
                    <Badge
                      size="lg"
                      variant="filled"
                      color={invoiceData.invoice.is_valid_for_return ? "green" : "red"}
                    >
                      {invoiceData.invoice.is_valid_for_return ? "Valid for Return" : "Expired (Over 5 Days)"}
                    </Badge>
                  </Group>
                  <Divider mb="md" />

                  <Group mb="md">
                    <ActionIcon size="xl" radius="xl" color="indigo" variant="light">
                      <IconUser size={24} />
                    </ActionIcon>
                    <Box>
                      <Text size="sm" color="dimmed">Customer Name</Text>
                      <Text fw={700} size="lg">{invoiceData.customer.first_name}</Text>
                      {invoiceData.customer.username && (
                        <Text size="xs" color="dimmed">ID / Mob: {invoiceData.customer.username}</Text>
                      )}
                    </Box>
                  </Group>
                </Box>

                {!invoiceData.invoice.is_valid_for_return ? (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Exchanges Not Allowed"
                    color="red"
                    radius="md"
                  >
                    This invoice was purchased {invoiceData.invoice.days_since_purchase} days ago. Store return policy restricts exchanges or refunds to within 5 days of purchase.
                  </Alert>
                ) : (
                  <Alert
                    icon={<IconCircleCheck size={16} />}
                    title="Eligible for Exchange"
                    color="green"
                    radius="md"
                  >
                    Purchased {invoiceData.invoice.days_since_purchase} days ago, making it eligible for immediate items return and exchange.
                  </Alert>
                )}
              </Card>
            </Grid.Col>
          </Grid>
      

          <Paper shadow="md" p="xl" radius="lg" mt={24} style={{ border: "1px solid rgba(226, 232, 240, 0.8)" }}>
            <Grid align="flex-end" mb="md">
              <Grid.Col span={12}>
                <Select
                  label="Search Product in Invoice"
                  placeholder="Select product to return"
                  searchable
                  clearable
                  nothingFound="No products found in this invoice"
                  data={invoiceData.items.map(item => ({
                    value: item.id.toString(),
                    label: `${item.product_title}`
                  }))}
                  value={productSearch}
                  onChange={(val) => {
                    if (val) {
                      handleSelectItem(val, true);
                      setProductSearch(null); // Reset select after picking
                    }
                  }}
                  size="lg"
                  radius="md"
                />
              </Grid.Col>
            </Grid>

            <Text fw={700} size="lg" mb="md" style={{ color: "#1A202C" }}>
              Selected Items for Return
            </Text>
            <Divider mb="md" />

            <Table verticalSpacing="sm" horizontalSpacing="md" striped highlightOnHover>
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>Select</th>
                  <th>Product</th>
                  <th>Batch No</th>
                  <th style={{ textAlign: "right" }}>Purchased Qty</th>
                  <th style={{ textAlign: "right" }}>Selling Price</th>
                  <th style={{ textAlign: "right" }}>Exchange Qty</th>
                  <th style={{ textAlign: "right" }}>Returned Total</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.items.filter(item => selectedItems[item.id]).map(item => {
                  const isChecked = selectedItems[item.id] || false;
                  const retQty = returnQtys[item.id] || 0;
                  const refundAmount = retQty * item.rate;

                  return (
                    <tr key={item.id}>
                      <td>
                        <Checkbox
                          checked={isChecked}
                          disabled={!invoiceData.invoice.is_valid_for_return}
                          onChange={e => handleSelectItem(item.id, e.target.checked)}
                          size="md"
                        />
                      </td>
                      <td>
                        <Box>
                          <Text fw={600} size="sm">{item.product_title}</Text>
                          <Text size="xs" color="dimmed">{item.product_code}</Text>
                        </Box>
                      </td>
                      <td>
                        <Badge variant="outline" color="dark">{item.batch_code || "N/A"}</Badge>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Text fw={500}>{item.qty} {item.units}</Text>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Text fw={500}>₹ {item.rate.toFixed(2)}</Text>
                      </td>
                      <td style={{ textAlign: "right", width: "160px" }}>
                        <NumberInput
                          value={retQty}
                          onChange={val => handleQtyChange(item.id, val, item.qty)}
                          precision={3}
                          min={0}
                          max={item.qty}
                          step={item.units?.toLowerCase() === "pcs" ? 1 : 0.001}
                          disabled={!isChecked || !invoiceData.invoice.is_valid_for_return}
                          radius="md"
                          size="xs"
                        />
                      </td>
                      <td style={{ textAlign: "right", color: isChecked ? "teal" : "#718096" }}>
                        <Text fw={600}>₹ {refundAmount.toFixed(2)}</Text>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <Divider my="xl" />

            <Group position="apart">
              <Box>
                <Text size="sm" color="dimmed">Total Return Value Credit:</Text>
                <Text size="xl" fw={800} style={{ color: "#2F855A" }}>
                  ₹ {calculateTotalRefund().toFixed(2)}
                </Text>
              </Box>

              <Button
                size="lg"
                radius="md"
                disabled={!invoiceData.invoice.is_valid_for_return || calculateTotalRefund() === 0}
                onClick={handleProceedExchange}
                rightIcon={<IconArrowRight size={18} />}
                style={{
                  backgroundColor: "#10B981",
                  "&:hover": {
                    backgroundColor: "#059669",
                  },
                }}
              >
                Proceed to Exchange Bill
              </Button>
            </Group>
          </Paper>
        </Box>
      )}
      </Container>
    </Box>
  );
}
